import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PhoneCall, Upload, ShieldAlert, Lock, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const VIOLATION_CATEGORIES = [
  "Penipuan / Penggelapan Donasi",
  "Pelecehan / Kekerasan Seksual & Fisik",
  "Pungli & Pemerasan",
  "Diskriminasi & Eksploitasi Relawan",
];

function EvidencePicker({ evidenceFile, onPick, onRemove }) {
  if (evidenceFile) {
    return (
      <div data-testid="evidence-preview" className="flex items-center gap-3 border border-slate-200 bg-slate-50 rounded-xl p-3">
        {evidenceFile.isImage ? (
          <img src={evidenceFile.url} alt={`Pratinjau bukti: ${evidenceFile.name}`} className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
        ) : (
          <span className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
            <FileText className="w-6 h-6 text-slate-400" aria-hidden="true" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{evidenceFile.name}</p>
          <p className="text-xs text-slate-400">{(evidenceFile.size / 1024).toFixed(0)} KB — bukti terlampir</p>
        </div>
        <button
          data-testid="evidence-remove-btn"
          onClick={onRemove}
          aria-label="Hapus lampiran bukti"
          className="p-2 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <label
      data-testid="report-evidence-upload"
      className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-4 cursor-pointer hover:border-emerald-400 transition-colors focus-within:ring-2 focus-within:ring-emerald-200"
    >
      <Upload className="w-5 h-5 text-slate-400" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-700">Unggah Bukti (foto, tangkapan layar, dokumen)</p>
        <p className="text-xs text-slate-400">Maks. 10MB — JPG, PNG, PDF</p>
      </div>
      <input type="file" accept="image/*,.pdf" className="sr-only" aria-label="Unggah file bukti" onChange={onPick} />
    </label>
  );
}

export default function ReportModal({ open, onOpenChange, targetType = "Komunitas", targetName = "" }) {
  const [category, setCategory] = useState("");
  const [anonymous, setAnonymous] = useState(true);
  const [contact, setContact] = useState("");
  const [description, setDescription] = useState("");
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 10MB.");
      return;
    }
    if (evidenceFile?.url) URL.revokeObjectURL(evidenceFile.url);
    setEvidenceFile({
      name: f.name,
      size: f.size,
      isImage: f.type.startsWith("image/"),
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
    });
  };

  const removeFile = () => {
    if (evidenceFile?.url) URL.revokeObjectURL(evidenceFile.url);
    setEvidenceFile(null);
  };

  const submit = async () => {
    if (!category || !description.trim()) {
      toast.error("Pilih kategori pelanggaran dan isi deskripsi kejadian.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_name: targetName || "Umum",
        category,
        anonymous,
        contact: anonymous ? "" : contact,
        description,
        evidence_name: evidenceFile?.name || "",
      });
      toast.success("Laporan terkirim secara rahasia. Tim Trust & Safety akan meninjau dalam 1x24 jam.");
      onOpenChange(false);
      setCategory(""); setDescription(""); setContact(""); removeFile(); setAnonymous(true);
    } catch {
      toast.error("Gagal mengirim laporan. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-modal" className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <div data-testid="emergency-banner" className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex gap-3">
          <PhoneCall className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <p className="text-sm text-rose-800 font-semibold leading-relaxed">
            Jika Anda berada dalam bahaya fisik langsung, segera hubungi{" "}
            <span className="font-black">Hotline Darurat 110</span> atau{" "}
            <span className="font-black">Layanan SAPA 129</span> (Telepon/WhatsApp).
          </p>
        </div>

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-slate-900">
            <ShieldAlert className="w-5 h-5 text-rose-600" /> Laporkan Masalah / Pengaduan
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Terkait: <span className="font-semibold text-slate-700">{targetType} — {targetName || "Umum"}</span>.
            Laporan Anda ditangani secara rahasia oleh tim Trust & Safety.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold text-slate-700 mb-1.5 block">Kategori Pelanggaran</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="report-category-select" className="rounded-xl">
                <SelectValue placeholder="Pilih kategori pelanggaran..." />
              </SelectTrigger>
              <SelectContent>
                {VIOLATION_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`report-category-option-${VIOLATION_CATEGORIES.indexOf(c)}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-sm font-bold text-slate-800">{anonymous ? "Anonim (Rahasia)" : "Sertakan Kontak untuk Tindak Lanjut"}</p>
                <p className="text-xs text-slate-500">Identitas pelapor dilindungi penuh.</p>
              </div>
            </div>
            <Switch data-testid="report-anonymous-switch" checked={!anonymous} onCheckedChange={(v) => setAnonymous(!v)} />
          </div>

          {!anonymous && (
            <Input
              data-testid="report-contact-input"
              placeholder="Email atau nomor WhatsApp Anda"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              className="rounded-xl"
            />
          )}

          <Textarea
            data-testid="report-description-input"
            placeholder="Jelaskan kronologi kejadian secara detail (kapan, di mana, siapa yang terlibat)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="rounded-xl min-h-[100px]"
          />

          <EvidencePicker evidenceFile={evidenceFile} onPick={pickFile} onRemove={removeFile} />

          <p className="text-xs text-slate-400 leading-relaxed">
            Dengan mengirim laporan ini, Anda menyatakan informasi yang diberikan benar. Laporan palsu dapat dikenakan sanksi.
            Data Anda dienkripsi dan hanya diakses tim moderasi resmi.
          </p>

          <Button
            data-testid="report-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold"
          >
            {submitting ? "Mengirim..." : "Kirim Laporan Rahasia"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
